// ─────────────────────────────────────────────────────────────────────────────────────────────
// finance.js — Motor FINANCIERO a 25 años (grado ingeniería): flujo de caja, VPN, TIR, LCOE y
// beneficios fiscales de la Ley 1715/2014. Funciones PURAS (sin red/React) → testeables.
// Modela: producción con degradación, ahorro con inflación tarifaria, O&M, y el incentivo de renta
// de la Ley 1715 (deducción especial del 50% de la inversión en FNCER). Referencial — la
// liquidación fiscal final la valida un contador; aquí es para comparar escenarios.

// TIR por bisección sobre los flujos (flows[0] = -capex). Robusto, sin dependencias.
export function tirBiseccion(flows, lo = -0.9, hi = 1.5, iter = 200) {
  const npvAt = (r) => flows.reduce((acc, f, i) => acc + f / Math.pow(1 + r, i), 0);
  let a = lo, b = hi, fa = npvAt(a), fb = npvAt(b);
  if (fa * fb > 0) return null; // sin cambio de signo → TIR no acotada en el rango
  for (let k = 0; k < iter; k++) {
    const m = (a + b) / 2, fm = npvAt(m);
    if (Math.abs(fm) < 1e-3) return m;
    if (fa * fm < 0) { b = m; fb = fm; } else { a = m; fa = fm; }
  }
  return (a + b) / 2;
}

/**
 * SUPUESTOS financieros por defecto. Están acá y NO transcritos en la UI a propósito: el resultado
 * viaja con los supuestos que de verdad se usaron (`supuestos` del retorno), así que un texto que
 * diga "descuento 10%" no puede quedar mintiendo cuando alguien cambie el número. Un VPN/TIR sin
 * sus supuestos a la vista no es un dato: es una cifra que el cliente no puede auditar.
 */
export const SUPUESTOS_DEFECTO = {
  tarifaInflPct: 0.06,     // inflación de la tarifa eléctrica
  degradacionPct: 0.005,   // degradación anual del módulo (IEC 61215)
  omPctCapex: 0.01,        // O&M anual como % del capex
  inflOMPct: 0.03,         // inflación del O&M
  discountPct: 0.10,       // tasa de descuento para VPN/LCOE
  years: 25,
  rentaMarginalPct: 0.35,  // tasa marginal de renta para valorar la deducción
  aniosDeduccion: 5,       // años en que se reparte la deducción de la Ley 1715
};

/**
 * Flujo de caja a `years` años.
 * opts: capexCop, savingsY1 (ahorro año 1, COP), prodY1Kwh, ley1715 (bool) + cualquier supuesto
 * de `SUPUESTOS_DEFECTO` que se quiera sobreescribir.
 */
export function cashflow25(opts = {}) {
  const capex = Number(opts.capexCop) || 0;
  const savingsY1 = Number(opts.savingsY1) || 0;
  const prodY1 = Number(opts.prodY1Kwh) || 0;
  const D = SUPUESTOS_DEFECTO;
  const inflTarifa = opts.tarifaInflPct ?? D.tarifaInflPct;
  const degr = opts.degradacionPct ?? D.degradacionPct;
  const omPct = opts.omPctCapex ?? D.omPctCapex;
  const inflOM = opts.inflOMPct ?? D.inflOMPct;
  const disc = opts.discountPct ?? D.discountPct;
  const years = opts.years ?? D.years;
  const ley1715 = opts.ley1715 !== false;
  const rentaMarg = opts.rentaMarginalPct ?? D.rentaMarginalPct;
  const aniosDed = opts.aniosDeduccion ?? D.aniosDeduccion;

  // Ley 1715: deducción especial de renta del 50% de la inversión en FNCER (hasta 50% de la renta
  // líquida/año, repartible en hasta 15 años). Valor fiscal ≈ 50%·capex·tasa marginal; lo
  // distribuimos en `aniosDed` (simplificado).
  const beneficioFiscal = ley1715 ? 0.5 * capex * rentaMarg : 0;
  const fiscalAnual = aniosDed > 0 ? beneficioFiscal / aniosDed : 0;

  const flujo = [];
  let npv = -capex, acum = -capex, acumDesc = -capex;
  let paybackNominal = null, paybackDescontado = null;
  let costDisc = capex, enerDisc = 0;

  for (let y = 1; y <= years; y++) {
    const prod = prodY1 * Math.pow(1 - degr, y - 1);
    const savings = savingsY1 * Math.pow(1 + inflTarifa, y - 1) * Math.pow(1 - degr, y - 1);
    const om = capex * omPct * Math.pow(1 + inflOM, y - 1);
    const fiscal = y <= aniosDed ? fiscalAnual : 0;
    const neto = savings - om + fiscal;
    const netoDesc = neto / Math.pow(1 + disc, y);
    npv += netoDesc;
    acum += neto;
    acumDesc += netoDesc;
    costDisc += om / Math.pow(1 + disc, y);
    enerDisc += prod / Math.pow(1 + disc, y);
    if (paybackNominal === null && acum >= 0) paybackNominal = y;
    if (paybackDescontado === null && acumDesc >= 0) paybackDescontado = y;
    flujo.push({
      year: y, prod: Math.round(prod), savings: Math.round(savings), om: Math.round(om),
      fiscal: Math.round(fiscal), neto: Math.round(neto), acumulado: Math.round(acum),
    });
  }

  const flows = [-capex, ...flujo.map((f) => f.neto)];
  const tir = tirBiseccion(flows);
  const lcoe = enerDisc > 0 ? costDisc / enerDisc : 0; // COP/kWh nivelado
  const ahorro25 = flujo.reduce((a, f) => a + f.savings, 0);

  return {
    npv: Math.round(npv),
    tir: tir != null ? +(tir * 100).toFixed(1) : null, // %
    lcoe: Math.round(lcoe),                            // COP/kWh
    paybackNominal, paybackDescontado,
    beneficioFiscal: Math.round(beneficioFiscal),
    ahorro25: Math.round(ahorro25),
    flujo,
    // Los supuestos REALMENTE aplicados viajan con el resultado → la nota al pie del informe se
    // arma con esto, no con números tecleados a mano que se despegan del motor.
    supuestos: {
      tarifaInflPct: inflTarifa, degradacionPct: degr, omPctCapex: omPct, inflOMPct: inflOM,
      discountPct: disc, years, ley1715, rentaMarginalPct: rentaMarg, aniosDeduccion: aniosDed,
    },
  };
}

export default { cashflow25, tirBiseccion, SUPUESTOS_DEFECTO };
